import React, { Component, useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import classnames from 'classnames';
import { Formik, Form } from 'formik';
import ms from 'ms';

import {
  actionDataTableRemoveItem,
} from 'client/helpers/dataTables';

import {
  hideFieldIf,
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import {
  hasRoleAnonymous,
  hasRoleAdmin,
} from 'common/userRoles';

import { isListingFullService, isListingTenantScreening } from 'common/listingHelpers';

import { getHouseholdQualification } from 'common/householdApplication/prequalificationHelpers';

import { formatPhoneNumber } from 'common/formatters';

import {
  SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_ACCEPTED,
  SR_API_ACTION_HOUSEHOLD_LISTING_REJECT,
  SR_API_ACTION_HOUSEHOLD_LISTING_UNARCHIVE,
} from 'api/actionTypes';

import {
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED_BY_PARTNER,
  HOUSEHOLD_ARCHIVE_REASON_OTHER,
  HOUSEHOLD_LISTING_ARCHIVE_REASON_OPTION_ITEMS,
} from 'common/householdConstants';

import {
  PARTNER_TYPE_AGENT,
  PARTNER_ID_STONE_OAK,
} from 'common/partnerConstants';

import {
  isApplicationPastStatus,
  makeApplicationStatusList,
  getLastApplicationCompletedDate,
  getHouseholdEarliestApplicationStatus,
  getApplicantsList,
  sortHouseholdApplicationsByLeadRenter,
} from 'common/householdApplication/applicationHelpers';

import {
  getDownloadHouseholdDocumentsUrl,
} from 'common/householdHelpers';

import withApiState from 'client/helpers/withApiState';
import HouseholdApplications from 'client/actions/Household';

import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLabel, {
  SR_LABEL_THEME_ORANGE_INFO_RECTANGLE,
} from 'client/components/SrLabel/SrLabel';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_THEME_TRANSPARENT,
  SR_BUTTON_HEIGHT_TALL,
} from 'client/components/SrButton/SrButton';
import SrFormikField, {
  SR_FIELD_THEME_PARTNER_FORM,
} from 'client/components/SrFormikField';
import SrModalV2, {
} from 'client/components/SrModalV2/SrModalV2';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrTextWithLinks from 'client/components/SrTextWithLinks';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrHouseholdPrequalSummary from 'client/components/SrHouseholdPrequalSummary';
import SrHouseholdDownloadDocumentsButton from 'client/components/SrHouseholdDownloadDocumentsButton';
import SrTimerWarning from 'client/components/SrTimerWarning';
import SrCardToggle from 'client/components/SrCardToggle/SrCardToggle';
import SrLink from 'client/components/SrLink/SrLink';
import SrArchiveAppModalSunScreen from 'client/components/SrArchiveAppModalSunScreen/SrArchiveAppModalSunScreen';
import SrArchiveSuccessModalSunScreen from 'client/components/SrArchiveAppModalSunScreen/SrArchiveSuccessModalSunScreen';
import SrUnArchiveAppModalSunScreen from 'client/components/SrUnArchiveAppModalSunScreen';

import postWithState from 'client/actions/postWithState';
import { INCOME_VERIFICATION_DOCS_EXPIRATION_IN_DAYS } from 'common/incomeVerificationConstants';
import SrWarningOnApprovalApplicationModal from './SrWarningOnApprovalApplicationModal';

function getFormFields({ isFullServiceListing }) {
  return [
    hideFieldIf(isFullServiceListing, {
      id: 'rejectedForCreditReport',
      title: 'Are you denying the applicant because of information from their credit report?',
      type: 'yes_no',
      validations: ['required'],
      classnames: 'deny-title partnerForm adobe',
    }),
    {
      id: 'denyReason',
      title: 'note for the applicants',
      properties: {
        placeholder: 'Please tell the applicants why they are denied.',
      },
      type: 'long_text',
      validations: ['required'],
    },
  ];
}

const getHouseholdCardMoment = ({
  household,
  householdListing,
  householdApplications,
  isArchived,
}) => {
  if (isArchived) {
    if (!householdListing.archivedDate) {
      return null;
    }

    return momentWithAustinTimezone(householdListing.archivedDate);
  }

  const householdCardDate = (
    household.acceptedDate
    || householdListing.sentToPartnerDate
    || getLastApplicationCompletedDate(householdApplications)
  );
  if (!householdCardDate) {
    return null;
  }
  return momentWithAustinTimezone(householdCardDate);
};

const getHouseholdCardDateFormmated = (householdCardMoment) => {
  if (!householdCardMoment) {
    return '';
  }

  const householdCardDateFormatted = householdCardMoment.calendar(null, {
    sameDay: '[Today]',
    lastDay: '[Yesterday]',
    lastWeek: 'MMM Do',
    sameElse: 'MMM Do',
  });

  return householdCardDateFormatted;
};

const householdAcceptedAplicationStatusList = makeApplicationStatusList({
  fromApplicationStatus: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
});

const SrHouseholdNotes = ({
  householdNotes,
}) => {
  const householdNote = householdNotes[0];
  const [isHouseholdNotesModalOpen, setIsHouseholdNotesModalOpen] = useState(false);

  return (
    <SrBox padding='8px 20px 10px' borderTop='1px solid rgba(123, 132, 136, 0.16)'>
      <SrText font='greycliff' weight='800'>
        App Updates {householdNote ? '' : '(No updates)'}
      </SrText>
      {householdNote && (
        <SrBox padding='7px 0 0' textOverflow='ellipsis' overflow='hidden'>
          <SrText size='14' weight='500' font='greycliff' color='greyMonochrome' lineHeight='20px'>
            {momentWithAustinTimezone(householdNote.createdDate).format('MMM D, YYYY [at] h:mm A')}
          </SrText>
          <SrBox padding='7px 0'>
            <SrText
              size='14'
              weight='500'
              font='greycliff'
              color='black'
              lineHeight='20px'
            >
              <SrTextWithLinks
                text={householdNote.note}
              />
            </SrText>
          </SrBox>
          {householdNotes.length > 1 && (
            <SrText
              weight='800'
              font='greycliff'
              color='blue300'
              lineHeight='25px'
              decoration='underline'
              cursor='pointer'
              onClick={() => setIsHouseholdNotesModalOpen(true)}
            >
              Show all {householdNotes.length} notes
            </SrText>
          )}
        </SrBox>
      )}
      {isHouseholdNotesModalOpen && (
        <SrModalV2
          buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
          showCloseButton={false}
        >
          <SrBox
            bgColor='white'
            borderRadius='24px'
          >
            <SrBox padding='32px 25px 19px'>
              <SrText
                font='greycliff'
                size='30'
                lineHeight='35px'
                weight='bolder'
                display='block'
              >
                App Updates
              </SrText>
            </SrBox>
            <SrBox padding='6px 25px 20px' maxHeight='372px' overflowY='scroll'>
              {householdNotes.map((householdNote) => {
                return (
                  <SrBox margin='0 0 16px'>
                    <SrText font='greycliff' size='14' weight='500' color='greyMonochrome' lineHeight='18px'>
                      {momentWithAustinTimezone(householdNote.createdDate).format('MMM D, YYYY [at] h:mm A')}
                    </SrText>
                    <SrText
                      display='block'
                      font='greycliff'
                      size='14'
                      weight='500'
                      color='black'
                      lineHeight='18px'
                    >
                      <SrTextWithLinks text={householdNote.note} />
                    </SrText>
                  </SrBox>
                );
              })}
            </SrBox>
            <SrBox padding='10px 25px 22px'>
              <SrButton
                onClick={() => setIsHouseholdNotesModalOpen(false)}
                theme={SR_BUTTON_THEME_ADOBE_GREY}
              >
                Cancel
              </SrButton>
            </SrBox>
          </SrBox>
        </SrModalV2>
      )}
    </SrBox>
  );
};

@withApiState(({ householdId }) => ({
  householdMarkApplicationsAcceptedApiState: {
    action: SR_API_ACTION_HOUSEHOLD_MARK_APPLICATIONS_ACCEPTED,
    queryId: `${householdId}`,
  },
}))
class SrPartnerHouseholdPendingApprovalCard extends Component {
  static propTypes = {
    householdListing: PropTypes.object.isRequired,
    household: PropTypes.object.isRequired,
    listing: PropTypes.object.isRequired,
    concession: PropTypes.object,
    history: PropTypes.object.isRequired,
    tenantScreeningCriteria: PropTypes.object.isRequired,
    renters: PropTypes.array.isRequired,
    householdApplications: PropTypes.array.isRequired,
    householdGuarantors: PropTypes.array.isRequired,
    guarantorApplications: PropTypes.array.isRequired,
    tenantAlertReports: PropTypes.array.isRequired,
    breedRestrictions: PropTypes.array.isRequired,
    title: PropTypes.string,
    appUrl: PropTypes.string.isRequired,
    dataSource: PropTypes.string,
    moveInDate: PropTypes.string.isRequired,
    currentUser: PropTypes.object.isRequired,
    partner: PropTypes.object.isRequired,
    onDeny: PropTypes.func,
    leaseStartDate: PropTypes.string,
    alwaysDenyButton: PropTypes.bool,
    hideDenyButton: PropTypes.bool,
    disableApproveApplicationButton: PropTypes.bool,
    onApprove: PropTypes.func,
    onUnArchive: PropTypes.func,
    hasMoreActiveApps: PropTypes.bool,
    applicationIsDenied: PropTypes.bool,
  }

  constructor(props) {
    super(props);

    this.state = {
      showDenyHouseholdModal: false,
      isRejectedHouseholdConfirmationModalOpen: false,
      isCompleteDepositInstructionsModalOpen: false,
      showUnApprovalHouseholdModal: false,
      showApproveHouseholdModal: false,
      isWarningOnApprovalApplicationModalOpen: false,
      showArchiveSuccessModal: false,
      showUnArchiveModal: false,
      archiveFormValues: {},
    };
  }

  _onShowApproveHousehold = () => {
    const {
      history,
      partner,
      householdListing,
      listing,
    } = this.props;

    if (isListingFullService(listing)) {
      const url = `/partner/${partner.slug}/listing/${householdListing.listingId}/approve/${householdListing.id}`; // eslint-disable-line max-len
      history.push(url);
    }
    else {
      this.setState({
        showApproveHouseholdModal: true,
        isWarningOnApprovalApplicationModalOpen: false,
      });
    }
  }

  _onValidatePendingVerifications = (householdQualification) => {
    if (householdQualification.inProgress) {
      this.setState({ isWarningOnApprovalApplicationModalOpen: true });
    }
    else {
      this._onShowApproveHousehold();
    }
  }

  _onCloseWarningOnApprovalModal = () => {
    this.setState({ isWarningOnApprovalApplicationModalOpen: false });
  }

  _onUnApproveHousehold = () => {
    this.setState({
      showApproveHouseholdModal: false,
      showUnApprovalHouseholdModal: true,
    });
  }

  _onCloseArchiveSuccessModal = () => {
    const { onDeny } = this.props;

    this.setState({ showArchiveSuccessModal: false, archiveFormValues: {} });

    if (onDeny) {
      onDeny();
    }
  }

  _onSubmitArchiveSunScreenForm = async (formValues) => {
    const { dispatch, dataSource, householdListing } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_LISTING_REJECT,
      queryId: `${householdListing.id}`,
      payload: {
        householdListingId: householdListing.id,
        cancelReason: formValues.cancelReason,
        rejectedReason: formValues.denyReason,
        rejectedForCreditReport: formValues.rejectedForCreditReport || false,
      },
    }).then(() => {
      this.setState({
        showDenyHouseholdModal: false,
        showArchiveSuccessModal: true,
        archiveFormValues: formValues,
      });

      if (dataSource) {
        dispatch(actionDataTableRemoveItem, {
          dataSource: dataSource,
          entityId: householdListing.id,
        });
      }
    });
  }

  _onSubmitUnArchiveSunScreenForm = async (formValues) => {
    const { dispatch, dataSource, householdListing, onUnArchive } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_LISTING_UNARCHIVE,
      queryId: `${householdListing.id}`,
      payload: {
        householdListingId: householdListing.id,
        unArchiveReason: formValues.unArchiveReason,
      },
    }).then(() => {
      this.setState({
        showUnArchiveModal: false,
      });

      if (dataSource) {
        dispatch(actionDataTableRemoveItem, {
          dataSource: dataSource,
          entityId: householdListing.id,
        });
      }

      if (onUnArchive) {
        onUnArchive();
      }
    });
  }

  _onShowUnArchiveAppModal = () => {
    this.setState({ showUnArchiveModal: true });
  }

  _onCloseUnArchiveAppModal = () => {
    this.setState({ showUnArchiveModal: false });
  }

  _onSubmitDenyHouseholdForm = (formValues) => {
    const { dispatch, dataSource, householdListing, onDeny } = this.props;

    this._onCloseDenyHouseholdApprovalModal();

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_LISTING_REJECT,
      queryId: `${householdListing.id}`,
      payload: {
        householdListingId: householdListing.id,
        cancelReason: HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED_BY_PARTNER,
        rejectedReason: formValues.denyReason,
        rejectedForCreditReport: formValues.rejectedForCreditReport || false,
      },
    }).then(() => {
      this.setState({
        isRejectedHouseholdConfirmationModalOpen: true,
      });

      if (dataSource) {
        dispatch(actionDataTableRemoveItem, {
          dataSource: dataSource,
          entityId: householdListing.id,
        });
      }

      if (onDeny) {
        onDeny();
      }
    });
  }

  _onShowDenyHouseholdApprovalModal = () => {
    this.setState({
      showDenyHouseholdModal: true,
    });
  }

  _onCloseDenyHouseholdApprovalModal = () => {
    this.setState({
      showDenyHouseholdModal: false,
    });
  }

  _onCloseApproveHouseholdModal = () => {
    this.setState({
      showUnApprovalHouseholdModal: false,
    });
  }

  _onCloseUndoneHouseholdApproval = () => {
    this.setState({
      showApproveHouseholdModal: false,
      showUnApprovalHouseholdModal: false,
    });
  }

  _onCloseRejectedHouseholdModal = () => {
    this.setState({
      isRejectedHouseholdConfirmationModalOpen: false,
    });
  }

  _downloadRenterDocs = () => {
    const {
      appUrl,
      householdListing,
    } = this.props;

    const downloadDocumentsUrl = getDownloadHouseholdDocumentsUrl({
      appUrl,
      listingId: householdListing.listingId,
      householdId: householdListing.householdId,
      regenerateDocs: true,
    });

    global.open(downloadDocumentsUrl);
  }

  _toggleCompleteInstructionsModal = () => {
    this.setState({
      isCompleteDepositInstructionsModalOpen: !this.state.isCompleteDepositInstructionsModalOpen,
    });
  }

  _goToFeeSettingsPage = () => {
    const { history, partner, listing, householdListing } = this.props;

    const queryParams = `householdListingIdToApprove=${householdListing.id}&listingIdToApprove=${listing.id}`;
    const redirectUrl = partner.type === PARTNER_TYPE_AGENT
      ? `/partner/${partner.slug}/listing/${listing.id}/edit/fee-settings?${queryParams}`
      : `/partner/${partner.slug}/settings/banks?${queryParams}`;

    history.push(redirectUrl);
  }

  _onApproveHousehold = () => {
    const { dispatch, householdListing, onApprove, applicationIsDenied = false } = this.props;

    dispatch(HouseholdApplications.approveApplications, {
      householdListingId: householdListing.id,
      updateHouseholdToActive: applicationIsDenied,
    }).then(() => {
      if (onApprove) {
        onApprove();
      }

      this.setState({
        showApproveHouseholdModal: false,
      });
    });
  }

  _getAllApplications = () => {
    const { householdApplications, guarantorApplications } = this.props;

    return [
      ...(householdApplications || []),
      ...(guarantorApplications || []),
    ];
  }

  _hasExpiredIncomeVerification = () => {
    const applications = this._getAllApplications();

    const applicationsWithExpiredReports = applications.filter((application) => {
      return !!application.incomeVerification?.deletedDate;
    });

    return applicationsWithExpiredReports.length > 0;
  }

  _hasAutomatedVOIEDocuments = () => {
    const applications = this._getAllApplications();
    return applications.some((application) => application.incomeVerification);
  }

  _getAutomatedVOIERemainingDays = () => {
    const hasAutomatedVOIEDocs = this._hasAutomatedVOIEDocuments();
    const applications = this._getAllApplications();

    if (!hasAutomatedVOIEDocs) {
      return null;
    }

    let earliestExpirationDate = null;

    for (const application of applications) {
      if (application.incomeVerification) {
        const { completedDate } = application.incomeVerification;
        const incomeVerificationExpirationDate = momentWithAustinTimezone(completedDate)
          .add(INCOME_VERIFICATION_DOCS_EXPIRATION_IN_DAYS, 'days');

        if (earliestExpirationDate === null) {
          earliestExpirationDate = incomeVerificationExpirationDate;
        }
        else if (earliestExpirationDate.isAfter(incomeVerificationExpirationDate)) {
          earliestExpirationDate = incomeVerificationExpirationDate;
        }
      }
    }

    const today = momentWithAustinTimezone();
    const limitDate = earliestExpirationDate.endOf('day');
    const remainingDays = limitDate.diff(today, 'days');

    return remainingDays || 0;
  }

  _getIncomeVerificationDocsTextComponent = () => {
    const hasIncomeVerificationDocuments = this._hasAutomatedVOIEDocuments();
    const remainingDays = this._getAutomatedVOIERemainingDays();

    if (!hasIncomeVerificationDocuments) {
      return;
    }

    const hasExpiredIncomeVerification = this._hasExpiredIncomeVerification();

    if (hasExpiredIncomeVerification || remainingDays <= 0) {
      return (
        <SrBox marginTop='24px' textAlign='center'>
          <SrText
            color='orange300'
            font='greycliff'
            size='14'
          >
            {
              `Income & credit report documents are
              ${INCOME_VERIFICATION_DOCS_EXPIRATION_IN_DAYS}+ days old and
              have expired for security reasons.`
            }
          </SrText>
        </SrBox>
      );
    }

    return (
      <SrBox marginTop='24px' textAlign='center'>
        <SrText
          font='greycliff'
          size='14'
        >
          {
            `Income & credit report documents will expire after
            ${INCOME_VERIFICATION_DOCS_EXPIRATION_IN_DAYS} days for security reasons.
            (${pluralize('days', remainingDays, true)} left)`
          }
        </SrText>
      </SrBox>
    );
  }

  _getNameFromApplication = (application) => {
    return `${application.firstName} ` +
      `${application.middleName ? `${application.middleName} ` : ''} ` +
      `${application.lastName}`;
  };

  _extractNamesFromApplications = (applications = []) => {
    return applications.map((app) => {
      return { name: this._getNameFromApplication(app) };
    });
  };

  _getShowDenyButton = ({ householdAccepted, isCurrentUserAnonymous }) => {
    const { hideDenyButton, alwaysDenyButton } = this.props;

    if (hideDenyButton) return false;

    return alwaysDenyButton ? true : (!householdAccepted && !isCurrentUserAnonymous);
  }

  _getApproveButtonText = ({
    isHouseholdOnPendingLandlordStatus,
    hasHouseholdNotPassPendingLandlordStatus,
  }) => {
    const { applicationIsDenied } = this.props;

    if (
      applicationIsDenied
      || (hasHouseholdNotPassPendingLandlordStatus || isHouseholdOnPendingLandlordStatus)
    ) {
      return 'Approve';
    }

    return 'Approved';
  }

  _getApproveButtonTheme = ({ householdAccepted, isDisableApproveApplicationButton }) => {
    const { disableApproveApplicationButton, applicationIsDenied } = this.props;

    if (applicationIsDenied && isDisableApproveApplicationButton) {
      return SR_BUTTON_THEME_TRANSPARENT;
    }

    if (isDisableApproveApplicationButton) {
      return disableApproveApplicationButton
        ? SR_BUTTON_THEME_ADOBE_GREY
        : SR_BUTTON_THEME_ADOBE_GREEN;
    }

    return householdAccepted
      ? SR_BUTTON_THEME_ADOBE_GREEN
      : SR_BUTTON_THEME_ADOBE_YELLOW;
  }

  render() {
    const {
      moveInDate,
      title,
      household,
      householdListing,
      concession,
      listing,
      tenantScreeningCriteria,
      renters,
      householdApplications,
      householdGuarantors,
      guarantorApplications,
      tenantAlertReports,
      breedRestrictions,
      householdMarkApplicationsAcceptedApiState,
      currentUser,
      partner,
      leaseStartDate,
      propertyManager,
      pendingLandlordDate,
      disableApproveApplicationButton,
      hasMoreActiveApps,
      applicationIsDenied,
    } = this.props;

    const {
      showUnApprovalHouseholdModal,
      showDenyHouseholdModal,
      isRejectedHouseholdConfirmationModalOpen,
      showApproveHouseholdModal,
      isWarningOnApprovalApplicationModalOpen,
      showArchiveSuccessModal,
      showUnArchiveModal,
      archiveFormValues,
    } = this.state;

    const householdApplicationStatus = getHouseholdEarliestApplicationStatus(householdApplications);
    const hasHouseholdNotPassPendingLandlordStatus = !isApplicationPastStatus({
      status: householdApplicationStatus,
    }, HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD);
    const isHouseholdOnPendingLandlordStatus = (
      HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD === householdApplicationStatus
    );
    const householdAccepted = !applicationIsDenied
      && householdAcceptedAplicationStatusList.includes(householdApplicationStatus);

    const householdQualification = getHouseholdQualification({
      moveInDate,
      listing,
      tenantScreeningCriteria: {
        ...tenantScreeningCriteria,
        breedRestrictions,
      },
      renters,
      householdApplications,
      householdGuarantors,
      guarantorApplications,
      tenantAlertReports,
      leadRenterId: household.leadRenterId,
      leaseStartDate,
      partner,
    });

    const householdNotes = household.householdNotes || [];

    const householdCardMoment = getHouseholdCardMoment({
      household,
      householdListing,
      householdApplications,
      isArchived: applicationIsDenied,
    });
    const householdDateFormatted = getHouseholdCardDateFormmated(householdCardMoment);

    const isCurrentUserAnonymous = hasRoleAnonymous(currentUser.roles);
    const isCurrentUserAdmin = hasRoleAdmin(currentUser.roles);
    const showDenyButton = this._getShowDenyButton({ householdAccepted, isCurrentUserAnonymous });
    const isDisableApproveApplicationButton = disableApproveApplicationButton || (
      householdMarkApplicationsAcceptedApiState.inProgress
      || householdAccepted
      || isCurrentUserAnonymous
    );
    const approveButtonTheme = this._getApproveButtonTheme({
      householdAccepted,
      isDisableApproveApplicationButton,
    });
    const approveButtonText = this._getApproveButtonText({
      isHouseholdOnPendingLandlordStatus,
      hasHouseholdNotPassPendingLandlordStatus,
    });

    const isFullServiceListing = isListingFullService(listing);
    const isTenantScreeningListing = isListingTenantScreening(listing);
    const shouldDisplayDownloadButton = !isCurrentUserAnonymous || partner.renterDocsPublic;
    const denyText = !isTenantScreeningListing
      ? 'Deny Application'
      : 'Archive Application';
    const subHeaderText = !applicationIsDenied
      ? `${householdAccepted ? 'Approved' : 'Submitted'} ${householdDateFormatted}`
      : `Archived ${householdDateFormatted}`;
    const getArchivedReason = () => {
      if (householdListing.cancelReason === HOUSEHOLD_ARCHIVE_REASON_OTHER) {
        return householdListing.rejectedReason;
      }

      const reason = HOUSEHOLD_LISTING_ARCHIVE_REASON_OPTION_ITEMS.find(
        (reason) => householdListing.cancelReason === reason.value
      );

      if (!reason) {
        return null;
      }

      return reason.label;
    };
    const archiveReasonLabelText = getArchivedReason();
    const archiveReasonLabel = isTenantScreeningListing && applicationIsDenied && archiveReasonLabelText && (
      <SrFlex marginBottom='12px' marginTop='12px' width='100%'>
        <SrLabel
          theme={SR_LABEL_THEME_ORANGE_INFO_RECTANGLE}
          label={archiveReasonLabelText}
        />
      </SrFlex>
    );

    const sortedHouseholdApplications = sortHouseholdApplicationsByLeadRenter({
      householdApplications,
      leadRenterId: household.leadRenterId,
    });

    const applicantsList = getApplicantsList({
      renters: this._extractNamesFromApplications(sortedHouseholdApplications),
      guarantors: this._extractNamesFromApplications(guarantorApplications),
    });

    const onTitleClick = () => {
      global.open(`/admin/household/all/${household.leadRenterId}`, '_blank');
    };

    const incomeVerificationDocsTextComponent = this._getIncomeVerificationDocsTextComponent();
    const hasAutomatedVOIEDocs = this._hasAutomatedVOIEDocuments();
    const automatedVOIERemainingDays = this._getAutomatedVOIERemainingDays();
    const hasExpiredVOIEDocs = this._hasExpiredIncomeVerification()
      || automatedVOIERemainingDays < 0;
    return (
      <div
        className={classnames(
          'SrPartnerHouseholdPendingApprovalCard',
          `pendingApprovalId-${household.id}`,
          {
            accepted: householdAccepted,
            denied: applicationIsDenied,
          },
        )}
      >
        {propertyManager && (
          <SrFlex bgColor='orange200' align='center' justify='between' padding='15px 20px'>
            <SrFlex align='center'>
              <SrIcon className='person-filled-orange' marginRight={8} />
              <SrText weight='bold' color='orange300'>
                {propertyManager.name}
              </SrText>
            </SrFlex>
          </SrFlex>
        )}
        <SrBox
          padding='18px 18px 8px 18px'
          className='SrPartnerHouseholdPendingApprovalCardHeader'
        >
          <SrText
            weight='800'
            color={!applicationIsDenied ? 'green' : 'greyMonochrome'}
            display='block'
            font='greycliff'
            fontSize='26px'
            lineHeight='25px'
            cursor={isCurrentUserAdmin ? 'pointer' : null}
            onClick={isCurrentUserAdmin ? onTitleClick : null}
          >
            {title || listing.addressStreetDisplay}
          </SrText>
          <SrFlex justify='between' align='end'>
            <SrBox marginTop='5px' width='100%'>
              <SrText
                color='blueGreycliff'
                opacity='0.5'
                font='greycliff'
                weight='800'
                fontSize='16px'
                lineHeight='25px'
              >
                {subHeaderText}
              </SrText>
              {archiveReasonLabel}
            </SrBox>
          </SrFlex>
          {isHouseholdOnPendingLandlordStatus
            && partner.id !== PARTNER_ID_STONE_OAK
            && (
              <SrBox marginTop='5px'>
                <SrTimerWarning
                  fromDate={momentWithAustinTimezone(pendingLandlordDate)}
                  tresholdSeconds={ms('1d') / 1000}
                  message='Renters waiting for you'
                />
              </SrBox>
            )}
          {hasMoreActiveApps && (
            <SrBox marginTop='5px'>
              <SrText weight='800' fontSize='16px' color='yellowGold'>
                This household has applied to other listings
              </SrText>
            </SrBox>
          )}
        </SrBox>
        <SrHouseholdNotes
          householdNotes={householdNotes}
        />
        <SrCardToggle
          title={
            `${renters.length + householdGuarantors.length} ` +
            `${pluralize('Applicants', renters.length + householdGuarantors.length)}:`
          }
          label={applicantsList}
        >
          <SrBox padding='0 20px' marginBottom='12px'>
            {sortedHouseholdApplications.map((application, index) => (
              <SrBox key={application.phone} marginTop={index > 0 ? '14px' : '-3px'}>
                <SrFlex justify='between' align='center'>
                  <SrText
                    weight='600'
                    fontSize='16px'
                    font='greycliff'
                  >
                    {this._getNameFromApplication(application)}
                  </SrText>
                  {isCurrentUserAdmin &&
                    <SrLink to={`/admin/household/all/${application.userId}`} target='_blank'>
                      <SrText
                        color='blue300'
                        weight='600'
                      >
                        Edit
                      </SrText>
                    </SrLink>
                  }
                </SrFlex>
                {(application.phone || application.email) &&
                  <SrFlex marginTop='5px' direction='col'>
                    {application.phone && (
                      <SrText
                        font='greycliff'
                        lineHeight='17px'
                        fontSize='14px'
                        color='grey'
                        weight='600'
                      >
                        {formatPhoneNumber(application.phone)}
                      </SrText>
                    )}
                    {application.email && (
                      <SrText
                        font='greycliff'
                        lineHeight='17px'
                        fontSize='14px'
                        color='grey'
                        weight='600'
                        paddingTop='2px'
                      >
                        {application.email}
                      </SrText>
                    )}
                  </SrFlex>
                }
              </SrBox>
            ))}
            {guarantorApplications.map((application) => (
              <SrBox key={application.phone} marginTop={renters.length > 0 ? '14px' : '-3px'}>
                <SrFlex justify='between'>
                  <SrText
                    weight='600'
                    fontSize='16px'
                    font='greycliff'
                  >
                    {this._getNameFromApplication(application)}
                  </SrText>
                  <SrBox marginLeft='10px'>
                    <SrText
                      fontStyle='italic'
                      color='grey'
                    >
                      Guarantor
                    </SrText>
                  </SrBox>

                </SrFlex>
                {(application.phone || application.email) &&
                  <SrFlex marginTop='5px' direction='col'>
                    {application.phone && (
                      <SrText
                        font='greycliff'
                        lineHeight='17px'
                        fontSize='14px'
                        color='grey'
                        weight='600'
                      >
                        {formatPhoneNumber(application.phone)}
                      </SrText>
                    )}
                    {application.email && (
                      <SrText
                        font='greycliff'
                        lineHeight='17px'
                        fontSize='14px'
                        color='grey'
                        weight='600'
                        paddingTop='2px'
                      >
                        {application.email}
                      </SrText>
                    )}
                  </SrFlex>}
              </SrBox>
            ))}
          </SrBox>
        </SrCardToggle>
        <SrBox className='SrPartnerHouseholdPendingApprovalCardBody'>
          <SrHouseholdPrequalSummary
            partner={partner}
            currentUser={currentUser}
            listing={listing}
            concession={concession}
            householdListing={householdListing}
            tenantScreeningCriteria={{
              ...tenantScreeningCriteria,
              breedRestrictions,
            }}
            rentersQualifications={householdQualification.rentersQualifications}
            householdRequirements={householdQualification.householdRequirements}
            rentersVerifications={householdQualification.rentersVerifications}
            guarantorsVerifications={householdQualification.guarantorsVerifications}
            householdVerifications={householdQualification.householdVerifications}
            householdApplications={householdApplications}
            guarantorApplications={guarantorApplications}
            tenantAlertReports={tenantAlertReports}
            householdAccepted={householdAccepted}
          />
          {isWarningOnApprovalApplicationModalOpen &&
            <SrWarningOnApprovalApplicationModal
              onClose={this._onCloseWarningOnApprovalModal}
              onButtonClick={this._onShowApproveHousehold}
            />
          }
          {shouldDisplayDownloadButton && (
            <>
              <SrHouseholdDownloadDocumentsButton
                listingId={listing.id}
                householdId={household.id}
                hasAutomatedVOIEDocs={hasAutomatedVOIEDocs}
                hasExpiredVOIEDocs={hasExpiredVOIEDocs}
                remainingDays={automatedVOIERemainingDays}
              />
              {incomeVerificationDocsTextComponent}
            </>
          )}
        </SrBox>
        <SrBox borderTop='1px solid rgba(123, 132, 136, 0.15)' paddingBottom='28px'>
          <SrBox
            marginTop='24px'
            padding='0 18px'
          >
            {
              !applicationIsDenied
                ? (
                  <SrButton
                    disabled={isDisableApproveApplicationButton}
                    theme={approveButtonTheme}
                    hasBoxShadow
                    heightType={SR_BUTTON_HEIGHT_TALL}
                    onClick={() => this._onValidatePendingVerifications(householdQualification)}
                  >
                    {approveButtonText}
                  </SrButton>
                )
                : (
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    heightType={SR_BUTTON_HEIGHT_TALL}
                    onClick={this._onShowUnArchiveAppModal}
                  >
                    Un-Archive Application
                  </SrButton>
                )
            }
          </SrBox>
          {showDenyButton && (
            <SrBox padding='0 18px' marginTop='23px'>
              <SrFlex justify='center'>
                <SrText
                  display='block'
                  align='center'
                  fontSize='13px'
                  lineHeight='25px'
                  weight='800'
                  font='greycliff'
                  color='blue'
                  decoration='underline-yellow'
                  cursor='pointer'
                  onClick={this._onShowDenyHouseholdApprovalModal}
                >
                  {denyText}
                </SrText>
              </SrFlex>
            </SrBox>
          )}
        </SrBox>
        {
          showUnApprovalHouseholdModal && (
            <SrSimpleMessageModal
              className='SrPartnerHouseholdUndoneApprovalMessage'
              iconUrl='/images/arrowCircleUndoOrange.svg'
              headline='Undone applicant approval!'
              message={
                'The applicants will not receive any message ' +
                'until you officially approve or deny them.'
              }
              buttonText='ok, thanks'
              maxWidth={375}
              onClose={this._onCloseUndoneHouseholdApproval}
              onButtonClick={this._onCloseUndoneHouseholdApproval}
            />
          )
        }
        {
          isRejectedHouseholdConfirmationModalOpen && (
            <SrSimpleMessageModal
              className='SrPartnerHouseholdUndoneApprovalMessage'
              iconUrl='/images/mail-send-green.svg'
              headline='Denial sent to applicants'
              message='If the applicants have any questions, we will reach out to you over email.'
              buttonText='ok, thanks'
              maxWidth={375}
              onClose={this._onCloseRejectedHouseholdModal}
              onButtonClick={this._onCloseRejectedHouseholdModal}
            />
          )
        }
        {
          showArchiveSuccessModal && (
            <SrArchiveSuccessModalSunScreen
              onClose={this._onCloseArchiveSuccessModal}
              archiveFormValues={archiveFormValues}
            />
          )
        }
        {
          showDenyHouseholdModal && !isTenantScreeningListing && (
            <SrDarkModal className='SrPartnerHouseholdDenyApprovalModal'>
              <SrModalContent>
                <Formik
                  validate={(formValues) => {
                    const formFields = getFormFields({ isFullServiceListing });
                    return createFormValidationFn(formFields)(formValues);
                  }}
                  initialValues={createInitialValues({
                    formFields: getFormFields({
                      isFullServiceListing,
                    }),
                  })}
                  onSubmit={this._onSubmitDenyHouseholdForm}
                  render={(formikBag) => {
                    const editPartnerListingApiState = {};
                    const formFields = getFormFields({
                      isFullServiceListing,
                    });

                    return (
                      <Form autoComplete='off'>
                        <div className='closeModalIcon' onClick={this._onCloseDenyApplicationModal} />
                        <div>
                          <div>
                            <SrText size='2xl' weight='800' lineHeight='36px' font='greycliff'>
                              Deny applicants for property:
                            </SrText>
                            <SrBox marginBottom='20px' marginTop='2px'>
                              <SrText fontSize='27px' weight='600' lineHeight='36px' font='greycliff'>
                                {listing.addressStreetDisplay}
                              </SrText>
                            </SrBox>
                          </div>
                          {formFields.map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              formField={formField}
                              formikBag={formikBag}
                              className={formField.classnames || SR_FIELD_THEME_PARTNER_FORM}
                            />
                          ))}
                          <SrBox className='buttons' paddingTop={9}>
                            <SrText
                              size='larger'
                              color='grey'
                              weight='medium'
                              cursor='pointer'
                              className='cancel'
                              onClick={this._onCloseDenyHouseholdApprovalModal}
                            >
                              Cancel
                            </SrText>
                            <ButtonDeprecated
                              type='submit'
                              className='submitButton'
                            >
                              deny
                            </ButtonDeprecated>
                          </SrBox>
                          {editPartnerListingApiState.error && (
                            <div className='formMessageError'>
                              <span>{editPartnerListingApiState.error}</span>
                            </div>
                          )}
                        </div>
                      </Form>
                    );
                  }}
                />
              </SrModalContent>
            </SrDarkModal>
          )
        }
        {
          showDenyHouseholdModal && isTenantScreeningListing && (
            <SrArchiveAppModalSunScreen
              onClose={this._onCloseDenyHouseholdApprovalModal}
              onArchive={this._onSubmitArchiveSunScreenForm}
            />
          )
        }
        {
          showUnArchiveModal && (
            <SrUnArchiveAppModalSunScreen
              onClose={this._onCloseUnArchiveAppModal}
              onUnArchive={this._onSubmitUnArchiveSunScreenForm}
            />
          )
        }
        {
          showApproveHouseholdModal && (
            <SrSimpleMessageModal
              className='SrPartnerHouseholdApprovedMessage'
              iconUrl='/images/illustration-confirm-thumbs-up.svg'
              headline='Confirm your approval'
              message={
                'Once approved, Sunroom will notify the applicants that they’re approved. ' +
                'Then you need to contact them to continue the underwriting process.'
              }
              buttonText='confirm approval'
              subLabelText='Undo, I do not want to approve them'
              maxWidth={375}
              onClose={this._onCloseApproveHouseholdModal}
              onButtonClick={this._onApproveHousehold}
              onSubLabelClick={this._onUnApproveHousehold}
            />
          )
        }
      </div>
    );
  }
}

SrPartnerHouseholdPendingApprovalCard.displayName = 'SrPartnerHouseholdPendingApprovalCard';

export default SrPartnerHouseholdPendingApprovalCard;
